import Phaser from 'phaser';

const SKY_TOP = 0x2e1065;
const SKY_MID = 0xc026d3;
const SKY_BOT = 0xf97316;

const TOWER_DARK = 0x0f172a;
const TOWER_MID = 0x1e293b;
const TOWER_GLASS = 0x334155;
const WINDOW = 0x93c5fd;
const LOGO_GOLD = 0xfbbf24;
const LOGO_GLOW = 0xfde68a;
const STAR = 0xffffff;

const CITY_DARK = 0x050812;
const CITY_MID = 0x0b1224;
const CITY_WINDOW = 0xfbbf24;

export function drawAvengersTowerSkyBackground(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 40));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.5) {
      const u = t / 0.5;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.5) / 0.5;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }

  const rand = mulberry32(777);
  g.fillStyle(STAR, 0.85);
  const starCount = Math.max(6, Math.floor(w / 80));
  for (let i = 0; i < starCount; i++) {
    const sx = rand() * w;
    const sy = rand() * h * 0.35;
    const sr = rand() < 0.3 ? 1.5 : 1;
    g.fillCircle(sx, sy, sr);
  }
}

export function drawAvengersTowerSilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  seed = 303,
) {
  const rand = mulberry32(seed);
  const baselineY = Math.floor(h * 0.82);

  g.fillStyle(CITY_DARK, 1);
  g.fillRect(0, baselineY, w, h - baselineY);

  const towerW = Math.min(Math.floor(w * 0.22), 200);
  const towerH = Math.floor(h * 0.68);
  const towerX = Math.floor((w - towerW) / 2);
  const towerY = baselineY - towerH;

  g.fillStyle(TOWER_DARK, 1);
  g.fillRect(towerX, towerY, towerW, towerH);

  const glassInset = Math.max(6, Math.floor(towerW * 0.1));
  g.fillStyle(TOWER_MID, 1);
  g.fillRect(
    towerX + glassInset,
    towerY + Math.floor(towerH * 0.08),
    towerW - glassInset * 2,
    towerH - Math.floor(towerH * 0.08),
  );

  const glassStripeW = Math.max(3, Math.floor(towerW * 0.08));
  g.fillStyle(TOWER_GLASS, 0.8);
  g.fillRect(
    towerX + Math.floor(towerW * 0.3),
    towerY + Math.floor(towerH * 0.1),
    glassStripeW,
    towerH - Math.floor(towerH * 0.15),
  );
  g.fillRect(
    towerX + Math.floor(towerW * 0.62),
    towerY + Math.floor(towerH * 0.1),
    glassStripeW,
    towerH - Math.floor(towerH * 0.15),
  );

  const windowRows = Math.max(6, Math.floor(towerH / 28));
  const windowCols = 3;
  for (let r = 0; r < windowRows; r++) {
    for (let c = 0; c < windowCols; c++) {
      if (rand() < 0.35) continue;
      const wx = towerX + glassInset + 4 + c * Math.floor((towerW - glassInset * 2) / windowCols);
      const wy = towerY + 16 + r * 26;
      if (wy > baselineY - 10) continue;
      g.fillStyle(rand() < 0.15 ? 0x64748b : WINDOW, 0.85);
      g.fillRect(wx, wy, 4, 6);
    }
  }

  const logoCx = towerX + towerW / 2;
  const logoCy = towerY + Math.floor(towerH * 0.28);
  const logoSize = Math.min(Math.floor(towerW * 0.5), 56);
  drawAvengersA(g, logoCx, logoCy, logoSize);

  const peakW = Math.floor(towerW * 0.55);
  const peakH = Math.floor(towerH * 0.08);
  g.fillStyle(TOWER_DARK, 1);
  g.fillTriangle(
    towerX + towerW / 2 - peakW / 2,
    towerY,
    towerX + towerW / 2 + peakW / 2,
    towerY,
    towerX + towerW / 2,
    towerY - peakH,
  );

  g.fillStyle(LOGO_GOLD, 1);
  g.fillRect(towerX + towerW / 2 - 1, towerY - peakH - 18, 2, 18);
  g.fillCircle(towerX + towerW / 2, towerY - peakH - 20, 2);

  drawCitySkyline(g, w, h, baselineY, rand);
}

function drawAvengersA(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  size: number,
) {
  const halfW = size * 0.45;
  const halfH = size * 0.55;
  const thickness = Math.max(3, Math.floor(size * 0.14));
  const outerHalo = Math.max(2, Math.floor(size * 0.2));

  g.fillStyle(LOGO_GLOW, 0.35);
  g.fillCircle(cx, cy, halfW + outerHalo);

  g.lineStyle(thickness, LOGO_GOLD, 1);
  g.beginPath();
  g.moveTo(cx - halfW, cy + halfH);
  g.lineTo(cx, cy - halfH);
  g.lineTo(cx + halfW, cy + halfH);
  g.strokePath();

  g.beginPath();
  g.moveTo(cx - halfW * 0.55, cy + halfH * 0.1);
  g.lineTo(cx + halfW * 0.55, cy + halfH * 0.1);
  g.strokePath();

  g.lineStyle(Math.max(1, Math.floor(thickness * 0.4)), LOGO_GLOW, 0.7);
  g.beginPath();
  g.moveTo(cx - halfW, cy + halfH);
  g.lineTo(cx, cy - halfH);
  g.lineTo(cx + halfW, cy + halfH);
  g.strokePath();
}

function drawCitySkyline(
  g: Phaser.GameObjects.Graphics,
  w: number,
  _h: number,
  baselineY: number,
  rand: () => number,
) {
  const maxBuildingH = Math.floor((baselineY) * 0.14);

  let x = -10;
  while (x < w + 10) {
    const bw = 18 + Math.floor(rand() * 40);
    const bh = Math.floor(maxBuildingH * (0.35 + rand() * 0.9));
    const bx = x;
    const by = baselineY - bh;

    const centralSkip = bx > w * 0.42 && bx < w * 0.58;
    if (!centralSkip) {
      g.fillStyle(rand() < 0.5 ? CITY_DARK : CITY_MID, 1);
      g.fillRect(bx, by, bw, bh + 4);

      const rows = Math.max(1, Math.floor(bh / 10));
      const cols = Math.max(2, Math.floor(bw / 8));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (rand() > 0.35) continue;
          const wx = bx + 3 + c * Math.floor(bw / cols);
          const wy = by + 3 + r * 10;
          if (wy > baselineY - 2) continue;
          g.fillStyle(CITY_WINDOW, 0.9);
          g.fillRect(wx, wy, 2, 3);
        }
      }
    }

    x += bw - Math.floor(rand() * 6);
  }
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
